/**
 * FoundingMemberRibbon
 *
 * Soft gold ribbon shown to a sister who has joined the launch challenge.
 * Used at the top of Today and on the launch challenge detail page.
 *
 * PRICING CLAIM REMOVED. This said "Half-off pricing, kept for life". Nothing
 * in the product could deliver that: checkout took a `founding` flag no caller
 * ever passed, and the coupon it would have applied was not configured on the
 * production Convex deployment. So the ribbon promised every launch-challenge
 * member a lifetime discount the code had no way to give. It now says what is
 * actually true — she was here at the start.
 *
 * To restore the offer for real: create a half-off promotion code in Stripe and
 * put the code in this copy. Checkout already accepts promotion codes
 * (convex/stripe.ts → createCheckout, allow_promotion_codes), so no app change
 * is needed beyond the wording.
 *
 * Tone: warm and grateful, never high-pressure.
 */
export default function FoundingMemberRibbon({
  count,
  endsLabel,
  variant = "card",
}: {
  /** Founding members joined so far (rough count, not a leaderboard). */
  count: number;
  /** Human-friendly window-close hint, e.g. "34 days left to join". */
  endsLabel: string;
  variant?: "card" | "inline";
}) {
  if (variant === "inline") {
    return (
      <span
        className="mnf-body"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 10px",
          borderRadius: 999,
          background: "var(--mnf-gold2)",
          color: "var(--mnf-gold)",
          border: "1px solid rgba(160,120,32,0.22)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
        data-testid="ribbon-founding-inline"
      >
        <span aria-hidden>{"\u2728"}</span>
        Founding member
      </span>
    );
  }

  return (
    <div
      className="mnf-fade-up"
      style={{
        background:
          "linear-gradient(135deg, rgba(160,120,32,0.12) 0%, rgba(245,240,232,0.7) 100%)",
        border: "1px solid rgba(160,120,32,0.30)",
        borderRadius: 18,
        padding: "12px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
      }}
      data-testid="ribbon-founding-card"
    >
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: "var(--mnf-gold)",
          color: "#fff",
          fontSize: 18,
        }}
        aria-hidden
      >
        {"\ud83c\udf42"}
      </span>
      <div className="min-w-0 flex-1">
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>
          Founding member
        </p>
        <p
          className="mnf-serif mt-0.5"
          style={{
            fontSize: 14,
            fontWeight: 500,
            color: "var(--mnf-fd)",
            lineHeight: 1.35,
          }}
        >
          Here from the very beginning
        </p>
        <p
          className="mnf-body mt-1"
          style={{
            fontSize: 11.5,
            color: "var(--mnf-tg)",
            lineHeight: 1.45,
          }}
        >
          {count} {count === 1 ? "sister has" : "sisters have"} joined &middot; {endsLabel}
        </p>
      </div>
    </div>
  );
}
